/**
 * PackIndex — the crawlable full-catalog index (pack-index, 2026-08-30). A closed-by-default
 * `<details>` listing every live pack as a plain `<a href="/{slug}">`, grouped under series
 * headings, server-rendered identically for every visitor (no UA branching, no scroll/click
 * required to reach the markup) — Google's spam policy explicitly permits collapsed/accordion
 * content read this way, unlike the `?page=N` chain this exists alongside (see `hubPagination.ts`
 * for that half of the fix). Rendered on `/packs` (below the grid) and on the home page (footer
 * region) of both products, so every live pack sits at depth 1 from both `/` and `/packs`.
 *
 * Presentational only — no classification logic lives here. `groups` arrives pre-computed
 * (`groupPacksBySeries()`, called from each route's `loader()`, server-only) so the sizeable
 * per-product taxonomy tables (`series/narcisSeries.server.ts`, `series/ovidSeries.server.ts`)
 * never enter the client bundle; this component only ever iterates the finished `SeriesGroup[]`.
 * Follows `PackHubToolbar`'s own established pattern: visual identity is an injected `styles`
 * CSS-module prop, not an imported stylesheet, so the shared component renders correctly against
 * either product's dark surface without knowing either token system.
 */

import type {SeriesGroup} from './series/types'

// Record<string, string> (not an exact-keys interface) — matches PackHubToolbarStyles's own
// convention: an imported `*.module.scss` resolves to the ambient `CSSModuleClasses` index-
// signature type, which isn't assignable to a narrower interface with required named keys.
export type PackIndexStyles = Record<string, string>

export function PackIndex({groups, total, styles}: {groups: SeriesGroup[]; total: number; styles: PackIndexStyles}) {
    if (total === 0) return null
    return (
        <details className={styles.root}>
            <summary className={styles.summary}>All {total} packs</summary>
            <div className={styles.groups}>
                {groups.map((group) => (
                    <div key={group.key} className={styles.group}>
                        <h3 className={styles.groupTitle}>{group.title}</h3>
                        <ul className={styles.list}>
                            {group.items.map((pack) => (
                                // No per-link `class` (pack-index, 2026-08-30) — at ~1,318 links,
                                // one repeated class attribute costs ~25 bytes × 1,318 ≈ 33 KB of
                                // the rendered HTML for nothing a `.list a` descendant selector
                                // can't do instead; see PackIndex.module.scss in each product.
                                <li key={pack.slug}>
                                    <a href={`/${pack.slug}`}>{pack.name}</a>
                                </li>
                            ))}
                        </ul>
                    </div>
                ))}
            </div>
        </details>
    )
}
